import React, { createContext, useContext, useEffect, useReducer } from "react";
import { authReducer } from "../reducers/authReducer";
import Constants from "../common/Constants";
import axios from "axios";
import Cookies from "js-cookie";

//Create context
const AuthContext = createContext();

//Initial state
const initialState = {
  isLoggedIn: false,
  user: null,
  authToken: null,
  ageSubmitted: false,
  baseLineQuizCompleted: false,
  myPoints: 0,
  studentProgress: null,
  isLoading: true,
  error: "",
  baseLineId: 0,
  endLineId: 0,
  surveyId: 0,
  activeQuizType: null
};
//Provider
const AuthProvider = ({ children }) => {
  const [authState, authDispatch] = useReducer(authReducer, initialState);



  const handleLogin = async (token) => {
    try {
      // Step 1: Check Baseline first — if it's Active, it's the pre-home gate (unchanged behavior).
      const baselineRes = await axios.post(`${Constants.BASE_URL}/baseline?access_token=${token}`);
      const baselineSummary = baselineRes?.data?.baseline_summary;

      let activeQuizType = null;
      let detailsStatusUrl = null;

      if (baselineSummary?.is_active) {
        if (!baselineSummary.baseline_id) {
          throw new Error("Baseline ID not found in response");
        }
        activeQuizType = 'baseline';
        const baseline_id = baselineSummary.baseline_id;
        authDispatch({ type: "SET_BASELINE_ID", payload: baseline_id });
        detailsStatusUrl = `${Constants.BASE_URL}/site/student-details-status?access_token=${token}&baseline_id=${baseline_id}`;
      } else {
        // Baseline is Inactive — Survey takes over the same gating slot if it's Active.
        const surveyRes = await axios.post(`${Constants.BASE_URL}/survey?access_token=${token}`);
        const surveySummary = surveyRes?.data?.survey_summary;

        if (surveySummary?.is_active) {
          if (!surveySummary.survey_id) {
            throw new Error("Survey ID not found in response");
          }
          activeQuizType = 'survey';
          const survey_id = surveySummary.survey_id;
          authDispatch({ type: "SET_SURVEY_ID", payload: survey_id });
          detailsStatusUrl = `${Constants.BASE_URL}/site/student-survey-details-status?access_token=${token}&survey_id=${survey_id}`;
        }
        // If Survey is also Inactive, activeQuizType stays null — nothing to gate on, straight to home.
      }

      authDispatch({ type: "SET_ACTIVE_QUIZ_TYPE", payload: activeQuizType });

      // Step 2: Parallel API calls after the active quiz (if any) is determined
      const [progressRes, statusRes, detailRes] = await Promise.all([
        axios.post(`${Constants.BASE_URL}/studentprogress?access_token=${token}`),
        detailsStatusUrl ? axios.post(detailsStatusUrl) : Promise.resolve(null),
        // Non-essential: it only refreshes the display name, so it must never be able
        // to fail the login flow the way a rejection inside Promise.all would.
        axios.post(`${Constants.BASE_URL}/student-detail?access_token=${token}`).catch(() => null)
      ]);

      const progress = progressRes?.data?.student_summary;
      const finalStatus = activeQuizType
        ? statusRes?.data?.student_summary?.final_submit_status === 1
        : true;

      // `user` is restored from the dcp_user cookie on every load and is otherwise
      // never refreshed, so anything changed in the MIS afterwards - the nick name in
      // particular - stayed stale until the student logged in again. Re-read it here,
      // where the token is already in hand, so a change lands on the next page load.
      const detail = detailRes?.data?.student_summary;
      if (detail?.student_name) {
        updateUser({ student_name: detail.student_name });
      }

      authDispatch({
        type: "SET_PROGRESS",
        payload: { points: progress?.points || 0, progress },
      });

      authDispatch({
        type: "SET_BASELINE_QUIZ_COMPLETED",
        payload: finalStatus,
      });

    } catch (error) {
      authDispatch({ type: "SET_ERROR", payload: error.message || "Login failed" });
    } finally {
      authDispatch({ type: "SET_LOADING", payload: false });
    }
  };


  const login = (user) => {
    const token = user.token;
    Cookies.set("dcp_auth_token", token);
    Cookies.set("dcp_user", JSON.stringify(user));
    // Re-arm isLoading before handleLogin resolves — otherwise, since this page
    // already has isLoading=false (set when it first showed the login form),
    // App.js briefly renders with default state (activeQuizType=null,
    // baseLineQuizCompleted=false) and falls through to <BaseLineQuiz/>, whose
    // own "no active questions" auto-finalize immediately marks the shared
    // completion flag true — hijacking it before handleLogin's real,
    // survey-aware check ever runs. The cookie-restore-on-refresh path doesn't
    // have this gap because isLoading starts true on a fresh mount.
    authDispatch({ type: "SET_LOADING", payload: true });
    authDispatch({ type: "LOGIN_SUCCESS", payload: { user, token } });
    handleLogin(token);
  };

  const logout = () => {
    Cookies.remove("dcp_auth_token");
    Cookies.remove("dcp_user");
    authDispatch({ type: "LOGOUT" });
  };

  const updateUser = (partialUser) => {
    authDispatch({ type: "UPDATE_USER", payload: partialUser });
    const userCookie = Cookies.get("dcp_user");
    if (userCookie) {
      try {
        const currentUser = JSON.parse(userCookie);
        Cookies.set("dcp_user", JSON.stringify({ ...currentUser, ...partialUser }));
      } catch {
        // ignore malformed cookie, in-memory state is still updated above
      }
    }
  };

  useEffect(() => {
    const token = Cookies.get("dcp_auth_token");
    const userCookie = Cookies.get("dcp_user");
    if (token && userCookie) {
      try {

        const user = JSON.parse(userCookie);
        authDispatch({ type: "LOGIN_SUCCESS", payload: { user, token } });
        handleLogin(token);
      } catch {
        logout();
      }
    } else {
      authDispatch({ type: "SET_LOADING", payload: false });
    }
  }, []);
  return (
    <AuthContext.Provider
      value={{
        ...authState,
        login,
        logout,
        updateUser,
        authDispatch,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

//hook
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

export default AuthProvider;
